/**
 * TypeScript interfaces matching the C# models in ClassifiedOrderEntryViewModel.cs
 * These interfaces mirror the data structures used by the AccountScout API
 */

// ============================================================================
// Ad Format Types
// ============================================================================

/**
 * Contains pertinent information about an ad format.
 * Determines how ad text and images are formatted in print.
 */
export interface AdFormatInfo {
  adFormatKey: number;
  name: string;
  description: string;
  maxNumberOfPrintImages: number;
  maxNumberOfWebFiles: number;
  /** A description of the ad format for online display */
  onlineDescription?: string;
  /** A base64-encoded example image showing what an ad with this format looks like */
  exampleImageBase64?: string;
}

// ============================================================================
// Advertiser Types
// ============================================================================

/**
 * Contains information about the party placing an order.
 * Used to create or locate an AccountScout advertiser.
 */
export interface AdvertiserInfo {
  /** External identifier (1-15 characters) */
  externalId?: string;
  /** Advertiser name (1-50 characters, required) */
  name: string;
  /** Street address line 1 (up to 40 characters) */
  street1?: string;
  /** Street address line 2 (up to 40 characters) */
  street2?: string;
  /** City (up to 35 characters) */
  city?: string;
  /** State (up to 25 characters) */
  state?: string;
  /** Country (up to 30 characters) */
  country?: string;
  /** Postal code (up to 15 characters) */
  postalCode?: string;
  /** Phone number (up to 20 characters) */
  phoneNumber?: string;
  /** Email address (up to 150 characters) */
  emailAddress?: string;
}

// ============================================================================
// Classification Types
// ============================================================================

/**
 * Represents a node in the classification tree.
 */
export interface TreeNode {
  data: ClassificationInfo;
  children: TreeNode[];
  level: number;
}

/**
 * Represents a tree of classifications with hierarchical structure.
 */
export interface ClassificationTree {
  roots: TreeNode[];
}

/**
 * Contains pertinent information about a classification.
 * Leaf classifications have endingNumber = null.
 * Group classifications have a number range (startingNumber to endingNumber).
 */
export interface ClassificationInfo {
  classificationKey: number;
  name: string;
  /** Starting number for groups, or assigned number for leaf classifications */
  startingNumber: number;
  /** Ending number for groups, null for leaf classifications */
  endingNumber: number | null;
}

/**
 * Association between a classification and an ad format.
 */
export interface ClassificationAdFormatAssociation {
  classificationKey: number;
  adFormatKey: number;
}

/**
 * Association between a classification and a custom field.
 */
export interface ClassificationCustomFieldAssociation {
  classificationKey: number;
  customFieldKey: number;
}

/**
 * Association between a classification and a package.
 */
export interface ClassificationPackageAssociation {
  classificationKey: number;
  packageKey: number;
}

// ============================================================================
// Custom Field Types
// ============================================================================

/**
 * Value types for custom fields (matching asCustomField.asValueType enum)
 */
export enum CustomFieldValueType {
  NotSpecified = -1,
  Integer = 0,
  Decimal = 1,
  Boolean = 2,
  String = 3,
  Memo = 4,
  FileName = 5,
  ListOfAllowedValues = 6,
}

/**
 * Contains pertinent information about a custom field.
 */
export interface CustomFieldInfo {
  customFieldKey: number;
  name: string;
  description: string;
  valueType: CustomFieldValueType;
  allowedValues?: CustomFieldAllowedValue[];
  isRequired: boolean;
  maximumLength?: number;
  relativeOrder: number;
  scale: number;
  useThousandsSeparator: boolean;
}

/**
 * Allowed value for a custom field with ListOfAllowedValues type.
 */
export interface CustomFieldAllowedValue {
  name: string;
  value: string;
}

/**
 * Value provided for a custom field.
 */
export interface CustomFieldValue {
  key: number;
  valueType: CustomFieldValueType;
  integerValue?: number;
  decimalValue?: number;
  booleanValue?: boolean;
  stringValue?: string;
  memoValue?: string;
  fileNameValue?: string;
  listOfAllowedValuesValue?: string;
}

// ============================================================================
// Feature Types
// ============================================================================

/**
 * Contains pertinent information about a feature.
 * Features are value-added services that affect pricing.
 */
export interface FeatureInfo {
  featureKey: number;
  name: string;
  description: string;
}

// ============================================================================
// Package Types
// ============================================================================

/**
 * Ad size unit types (matching asClassifiedOrder.asAdSizeUnitType enum)
 */
export enum AdSizeUnitType {
  NotSpecified = -1,
  Words = 0,
  Lines = 1,
  ColumnInches = 2,
  Inches = 3,
  ModularUnits = 4,
}

/**
 * Contains pertinent information about a package.
 * Determines where/when ads run and how they're priced.
 */
export interface PackageInfo {
  packageKey: number;
  name: string;
  description: string;
  fixedPrice?: number;
  maxUnitsAllowed?: number;
  maxUnitsType: AdSizeUnitType;
  constrainedCustomFieldFKey?: number;
  constrainedCustomFieldMaxValue?: number;
  publicationKeys: number[];
}

/**
 * Association between a package and a feature.
 */
export interface PackageFeatureAssociation {
  packageKey: number;
  featureKey: number;
}

// ============================================================================
// Publication Types
// ============================================================================

/**
 * Information about a publication.
 */
export interface PublicationInfo {
  publicationKey: number;
  name: string;
  abbreviation: string;
}

// ============================================================================
// Request Types
// ============================================================================

/**
 * Request to get scheduled ads.
 */
export interface GetScheduledAdsRequest {
  publicationDate: string; // ISO date string
  publications?: number[];
}

/**
 * Request to calculate the price for a classified ad order.
 */
export interface PriceOrderRequest {
  leafClassificationKey: number;
  groupingClassificationKey?: number; // Parent category group classification key
  packageKey: number;
  adFormatKey?: number;
  featureKeys?: number[];
  adText: string;
  adTextRTF?: string;
  startDate: string; // ISO date string
  printImages?: string[];
  /** Print images with file content as byte arrays */
  printImagesWithContent?: FileData[];
  /** Online/web images with file content as byte arrays */
  webFilesWithContent?: FileData[];
}

/**
 * Request to place an order for a classified ad.
 */
export interface PlaceOrderRequest extends PriceOrderRequest {
  webFiles?: string[];
  /** Online/web images with file content as byte arrays (uploaded to Azure) */
  webFilesWithContent?: FileData[];
  externalId?: string;
  advertiserInfo?: AdvertiserInfo;
  customFieldValueList?: CustomFieldValue[];
  description?: string;
  notes?: string;
  paymentAmount?: number;
  qrCode?: QrCodeConfig;
  /** Print images with file content as byte arrays */
  printImagesWithContent?: FileData[];
  // ePayment details (for credit card payments)
  /** Transaction/reference ID from the payment gateway */
  ePaymentTransactionId?: string;
  /** Authorization code from the payment gateway */
  ePaymentAuthorizationCode?: string;
  /** Last four digits of the account number */
  ePaymentPartialAccountNumber?: string;
  /** Account type (e.g., Visa, MasterCard, checking) */
  ePaymentAccountType?: string;
  /** Surcharge amount applied to the payment */
  surchargeAmount?: number;
  /** Pre-allocated advertiser key from ProcessPayment (for new advertisers with vaulted cards) */
  preAllocatedAdvertiserKey?: number;
  /** Cloudflare Turnstile CAPTCHA token for bot protection */
  captchaToken?: string;
}

/**
 * Authoritative result returned by ClassifiedScout order placement.
 */
export interface PlaceOrderResult {
  /** AccountScout order number assigned to the submission. */
  orderNumber: string;
  /** Whether this order was placed into staff review. */
  requiresStaffReview: boolean;
  /** Publisher-configured message shown while the order awaits review. */
  staffReviewConfirmationMessage?: string;
}

/**
 * QR code configuration for an ad.
 */
export interface QrCodeConfig {
  adUrl: string;
  embedQrCode: boolean;
}

/**
 * File data with content for uploading.
 */
export interface FileData {
  fileName: string;
  content: number[]; // byte array
}

// ============================================================================
// Response Types
// ============================================================================

/**
 * Response containing first and last run dates for an order.
 */
export interface GetOrderRunDatesResponse {
  startDate: string; // ISO date string
  endDate: string; // ISO date string
}

/**
 * Error information for scheduled ads retrieval.
 */
export interface GetScheduledAdsErrorInfo {
  runKey: number;
  orderNumber: string;
  publicationKey: number;
  classificationKey: number;
  errorDetail: string;
}

/**
 * Response containing scheduled ads and any errors.
 */
export interface GetScheduledAdsResponse {
  scheduledAds: ScheduledAdInfo[];
  errorList?: GetScheduledAdsErrorInfo[];
}

/**
 * Information about a scheduled ad run.
 */
export interface ScheduledAdInfo {
  classificationKey: number;
  adTextFormatted: string;
  adTextPlain: string;
  orderNumber: string;
  orderStartDate: string;
  orderEndDate: string;
  features: number[];
  customFieldValues: CustomFieldValue[];
  printImages: string[];
  webFiles: string[];
  isPdfBuilderAd: boolean;
  pdfBuilderAdCopy?: string; // Base64-encoded image string
  /** URL to the cached PDF Builder ad image in Azure Blob Storage (when pdfImageUrls=true) */
  pdfBuilderImageUrl?: string;
  /** Tenant default classified body font size, in points, used to calibrate PDF Builder card presentation. */
  pdfBuilderBaselineFontSizePt?: number;
  /** DPI used to render the PDF Builder image. */
  pdfBuilderRenderDpi?: number;
  imageCount: number;
}

/**
 * Information about a scheduled retail classified display ad.
 */
export interface ScheduledRetailClassDisplayAdInfo {
  classificationKey: number;
  orderNumber: string;
  orderStartDate: string;
  orderEndDate: string;
  adImageURL: string;
  adImageFilename: string;
}

/**
 * Response containing scheduled retail classified display ads.
 */
export interface GetScheduledRetailClassDisplayAdsResponse {
  scheduledDisplayAds: ScheduledRetailClassDisplayAdInfo[];
  errorList?: GetScheduledAdsErrorInfo[];
}

/**
 * Request for browsing ads within a date range around today.
 */
export interface BrowseAdsRequest {
  /** Number of days before today to include. Default is 6. */
  daysBeforeToday?: number;
  /** Number of days after today to include. Default is 7. */
  daysAfterToday?: number;
  /** Optional publication filter. Null explicitly delegates to globally enabled online publications. */
  publications?: number[] | null;
  /** Optional list of group classification keys to filter by. */
  allowedGroupClassificationKeys?: number[];
}

/**
 * Response containing scheduled ads and display ads for a date range.
 */
export interface BrowseAdsResponse {
  /** The start date of the date range queried. */
  startDate: string;
  /** The end date of the date range queried. */
  endDate: string;
  /** Regular classified ads scheduled within the date range. */
  scheduledAds: ScheduledAdInfo[];
  /** Retail classified display ads (business image ads) scheduled within the date range. */
  displayAds: ScheduledRetailClassDisplayAdInfo[];
  /** Any errors encountered while fetching ads. */
  errorList?: GetScheduledAdsErrorInfo[];
}

// ============================================================================
// Lazy Loading Types (for fast initial page load)
// ============================================================================

/**
 * Lightweight count info per classification.
 */
export interface ClassificationAdCount {
  classificationKey: number;
  regularAdCount: number;
  displayAdCount: number;
}

/**
 * Response for the browse counts endpoint - returns classification counts only.
 * Used for fast initial page load without fetching full ad details.
 */
export interface BrowseCountsResponse {
  /** The start date of the date range being counted. */
  startDate: string;
  /** The end date of the date range being counted. */
  endDate: string;
  /** List of ad counts per classification. */
  counts: ClassificationAdCount[];
}

/**
 * Request for fetching ads for a single classification.
 * Used for lazy loading when a classification is selected.
 */
export interface ClassificationAdsRequest {
  /** The classification key to fetch ads for. */
  classificationKey: number;
  /** Number of days before today to include. Default is 6. */
  daysBeforeToday?: number;
  /** Number of days after today to include. Default is 7. */
  daysAfterToday?: number;
  /** Optional publication filter. Null explicitly delegates to globally enabled online publications. */
  publications?: number[] | null;
  /** Number of items to skip (for pagination). Omit to load all. */
  skip?: number;
  /** Number of items to take (for pagination). Omit to load all. */
  take?: number;
  /** Optional search text to filter ads by content or custom field values. */
  searchText?: string;
}

/**
 * Response containing ads for a single classification.
 * Used for lazy loading when a classification is selected.
 */
export interface ClassificationAdsResponse {
  /** The classification key that was queried. */
  classificationKey: number;
  /** Regular classified ads for this classification. */
  scheduledAds: ScheduledAdInfo[];
  /** Retail classified display ads for this classification. */
  displayAds: ScheduledRetailClassDisplayAdInfo[];
  /** Any errors encountered while fetching ads. */
  errorList?: GetScheduledAdsErrorInfo[];
  /** Total count of matching ads (for pagination). 0 if not paginated. */
  totalCount?: number;
}

/**
 * Service information describing the active web service instance.
 */
export interface ServiceInfo {
  accountScoutUserName: string;
  advertiserSet: string;
  billingOffice: string;
  dataBaseName: string;
  defaultAdvertiser: string;
  salesRep: string;
  version: string;
  /** Whether new online submissions wait for staff approval before publication. */
  requiresStaffReview?: boolean;
  /** Publisher-configured message shown after a submission enters staff review. */
  staffReviewConfirmationMessage?: string;
}

// ============================================================================
// Payment Types
// ============================================================================

/**
 * Configuration needed to initialize the Celero payment tokenizer on the client.
 */
export interface PaymentConfig {
  /** The URL for the Celero tokenizer script */
  tokenizerUrl: string;
  /** The public API key for the Celero gateway */
  publicKey: string;
  /** Whether to use the sandbox/test environment */
  useSandbox: boolean;
  /** Whether surcharging is enabled for online payments */
  surchargingEnabled: boolean;
  /** The configured surcharge percentage (e.g., 3.0 for 3%) */
  surchargePercentage?: number;
  /** The public label for the surcharge fee (e.g., "Credit Card Surcharge") */
  surchargeLabel?: string;
}

/**
 * Billing address for payment processing.
 */
export interface BillingAddress {
  /** Street address line 1 */
  addressLine1?: string;
  /** Street address line 2 */
  addressLine2?: string;
  /** City */
  city?: string;
  /** State or province */
  state?: string;
  /** Postal/ZIP code */
  postalCode?: string;
  /** Country code (default: US) */
  country?: string;
}

/**
 * Request to process a credit card payment using a token from the Celero tokenizer.
 */
export interface ProcessPaymentRequest {
  /** The payment token obtained from the Celero tokenizer */
  token: string;
  /** The base amount to charge in dollars (before surcharge) */
  amount: number;
  /** The surcharge amount in dollars (if applicable) */
  surchargeAmount: number;
  /** Optional description for the transaction */
  description?: string;
  /** Optional customer email for receipt */
  customerEmail?: string;
  /** Optional billing address */
  billingAddress?: BillingAddress;
  /** Optional advertiser info for vaulting the card after payment */
  advertiserInfo?: AdvertiserInfo;
  /** Cloudflare Turnstile CAPTCHA token for bot protection */
  captchaToken?: string;
}

/**
 * Response from a payment processing request.
 */
export interface ProcessPaymentResponse {
  /** Whether the payment was successful */
  success: boolean;
  /** Status message describing the result */
  message: string;
  /** Transaction ID from the payment gateway (if available) */
  transactionId?: string;
  /** Authorization code (if payment was approved) */
  authorizationCode?: string;
  /** The actual surcharge amount applied by Celero (0 for debit cards) */
  actualSurchargeAmount?: number;
  /** The total amount actually charged (base + actual surcharge) */
  totalAmountCharged?: number;
  /** The last four digits of the card/account number */
  partialAccountNumber?: string;
  /** The type of account (e.g., Visa, MasterCard, checking) */
  accountType?: string;
  /** Whether the card was successfully saved to the vault */
  cardVaulted?: boolean;
  /** The vaulted payment method ID from Celero (if card was saved) */
  vaultedPaymentMethodId?: string;
  /** The pre-allocated advertiser key for a new advertiser (pass to PlaceOrder) */
  preAllocatedAdvertiserKey?: number;
}

/** Customer-safe result names returned by the combined paid checkout. */
export type PaidCheckoutOutcome =
  | 'Success'
  | 'RecoveredSuccess'
  | 'ValidationFailed'
  | 'PriceChanged'
  | 'PaymentDeclined'
  | 'SaveFailedReversed'
  | 'SaveFailedUnresolved';

/** Request used to validate a paid order before card entry. */
export interface PaidCheckoutPreflightRequest {
  checkoutAttemptId: string;
  expectedBaseAmount: number;
  order: PlaceOrderRequest;
}

/** Result of validating a paid order before card entry. */
export interface PaidCheckoutPreflightResponse {
  success: boolean;
  outcome: PaidCheckoutOutcome;
  message: string;
  baseAmount: number;
  supportReference?: string;
}

/** Complete server-controlled paid checkout request. */
export interface PaidCheckoutRequest extends PaidCheckoutPreflightRequest {
  token: string;
  billingAddress?: BillingAddress;
  customerEmail?: string;
  captchaToken?: string;
}

/** Structured result of the server-controlled paid checkout. */
export interface PaidCheckoutResponse {
  success: boolean;
  outcome: PaidCheckoutOutcome;
  message: string;
  retryAllowed: boolean;
  supportReference?: string;
  orderNumber?: string;
  requiresStaffReview?: boolean;
  staffReviewConfirmationMessage?: string;
  baseAmount: number;
  actualSurchargeAmount?: number;
  totalAmountCharged?: number;
}

// ============================================================================
// Store State Types
// ============================================================================

/**
 * State for the classified order store.
 */
export interface ClassifiedOrderState {
  // Data
  classifications: ClassificationInfo[];
  classificationsTree: ClassificationTree | null;
  packages: PackageInfo[];
  adFormats: AdFormatInfo[];
  features: FeatureInfo[];
  publications: PublicationInfo[];
  customFields: CustomFieldInfo[];
  
  // Associations
  classificationAdFormatAssociations: ClassificationAdFormatAssociation[];
  classificationCustomFieldAssociations: ClassificationCustomFieldAssociation[];
  classificationPackageAssociations: ClassificationPackageAssociation[];
  packageFeatureAssociations: PackageFeatureAssociation[];
  
  // UI State
  isLoading: boolean;
  error: string | null;
  
  // Order State
  calculatedPrice: number | null;
  lastOrderNumber: string | null;
  
  // Payment State
  paymentConfig: PaymentConfig | null;
  paymentProcessing: boolean;
  lastPaymentResponse: ProcessPaymentResponse | null;
}

// ============================================================================
// Site Configuration Types
// ============================================================================

/**
 * Configuration for a site within a tenant.
 * Sites allow separate publication scopes for placement and presentment, plus classification filtering.
 */
export interface SiteConfig {
  /** Unique identifier for the site within the tenant */
  siteId: string;
  /** Display name of the site */
  name: string;
  /** 
   * Publication keys used to limit Create Ad packages and, by default, browse presentment.
   * If null or empty, no site-level publication restriction is applied.
   */
  allowedPublicationKeys?: number[] | null;
  /**
   * Whether browse presentment ignores allowedPublicationKeys and uses all publications enabled
   * globally for online presentment. Missing values default to false.
   */
  presentAllPublications?: boolean;
  /**
   * List of group classification keys to display.
   * If null or empty, all classification groups are shown.
   */
  allowedGroupClassificationKeys?: number[] | null;
  /** Whether selecting a major or intermediate category shows listings from all of its minor categories. */
  majorCategoryOverviewEnabled?: boolean;
  /** Optional site override for tenant order creation; missing values inherit the tenant. */
  allowOrderCreation?: boolean;
  /** Optional theme override for this site */
  theme?: string;
  /** Optional browse-page title override. Empty string intentionally hides the title. */
  browseLabel?: string | null;
  /** Optional singular customer-facing listing label. */
  listingLabelSingular?: string | null;
  /** Optional plural customer-facing listing label. */
  listingLabelPlural?: string | null;
  /** Optional primary button/accent fill color as #rrggbb. */
  buttonBackgroundColor?: string | null;
  /** Optional primary/accent-filled text color as #rrggbb. */
  buttonTextColor?: string | null;
  /** Optional prominent accent line color as #rrggbb. */
  accentLineColor?: string | null;
  /** Optional customer-facing page background color as #rrggbb. */
  pageBackgroundColor?: string | null;
}

/**
 * Tenant configuration with optional sites.
 */
export interface TenantConfig {
  /** Unique identifier for the tenant */
  tenantId: string;
  /** Display name of the tenant */
  name: string;
  /** API key for AccountScout API access */
  apiKey: string;
  /** List of allowed domains for iframe embedding */
  allowedDomains: string[];
  /** Visual theme (dark/light) */
  theme: string;
  /** Whether to show header/footer in iframe */
  showHeader: boolean;
  /** Whether public configuration requests must identify a configured site. */
  requireSiteId?: boolean;
  /** Whether the tenant experience displays customer order-entry actions. */
  allowOrderCreation?: boolean;
  /** Optional browse-page title override. Empty string intentionally hides the title. */
  browseLabel?: string | null;
  /** Optional singular customer-facing listing label. */
  listingLabelSingular?: string | null;
  /** Optional plural customer-facing listing label. */
  listingLabelPlural?: string | null;
  /** Optional primary button/accent fill color as #rrggbb. */
  buttonBackgroundColor?: string | null;
  /** Optional primary/accent-filled text color as #rrggbb. */
  buttonTextColor?: string | null;
  /** Optional prominent accent line color as #rrggbb. */
  accentLineColor?: string | null;
  /** Optional customer-facing page background color as #rrggbb. */
  pageBackgroundColor?: string | null;
  /** Optional sites for multi-site configuration */
  sites?: SiteConfig[];
}
